/** Reuse unchanged branches of an immutable JSON document received over the wire.
 * Scene/render caches can then update one item or material without rebuilding every other mesh. */
export function shareEqual<T>(previous: T, incoming: T): T {
  if (previous === incoming) return previous;
  if (!previous || !incoming || typeof previous !== 'object' || typeof incoming !== 'object'
    || Array.isArray(previous) !== Array.isArray(incoming)) return incoming;
  const old = previous as Record<string, unknown>, next = incoming as Record<string, unknown>;
  const keys = Object.keys(next);
  const copy = (): Record<string, unknown> => Array.isArray(incoming)
    ? incoming.slice() as unknown as Record<string, unknown> : { ...next };
  let result: Record<string, unknown> | undefined = keys.length !== Object.keys(old).length
    || (Array.isArray(previous) && Array.isArray(incoming) && previous.length !== incoming.length) ? copy() : undefined;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = shareEqual(old[key], next[key]);
    if (!result && (value !== old[key] || !Object.hasOwn(old, key))) {
      // Allocate only after an actual change. Earlier siblings were equal and can
      // reuse the previous references; an equal subtree needs no output objects.
      result = copy();
      for (let j = 0; j < i; j++) result[keys[j]] = old[keys[j]];
    }
    // The shallow copy already has an own data property for every incoming key,
    // including __proto__; assigning cannot invoke Object.prototype's setter.
    if (result) result[key] = value;
  }
  return result ? result as T : previous;
}
